import * as _React from 'react'
import {FlatList, StyleSheet, Text, View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useNavigation} from '@react-navigation/native'

import {usePalette} from '#/lib/hooks/usePalette'
import {isWeb} from '#/platform/detection'
import {usePremium} from '#/state/premium'
import {useSession} from '#/state/session'
import {
  atoms as a,
  useBreakpoints,
  useLayoutBreakpoints,
  useTheme,
  web,
} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {useDialogControl} from '#/components/Dialog'
import {EditBig_Stroke2_Corner0_Rounded as EditBig} from '#/components/icons/EditBig'
import {VideoCamera_Filled_Corner0_Rounded as VideoCameraFilled} from '#/components/icons/VideoCamera'
import {VideoCamera_Stroke2_Corner0_Rounded as VideoCamera} from '#/components/icons/VideoCamera'
import * as Layout from '#/components/Layout'
import {CENTER_COLUMN_OFFSET} from '#/components/Layout'
import {Link} from '#/components/Link'
import {CreateRoomDialog} from './components/CreateRoomDialog'
import {RoomItem} from './components/RoomItem'
import {useRooms} from './hooks/useRooms'
import {RoomsPaywall} from './RoomsPaywall'

/**
 * Component that renders vertical border lines on the left and right sides of the content
 */
const BorderLines = () => {
  const t = useTheme()
  const {centerColumnOffset} = useLayoutBreakpoints()
  const {gtMobile} = useBreakpoints()

  if (!isWeb) return null

  // Only show borders on screens larger than mobile
  return gtMobile ? (
    <View
      style={[
        a.fixed,
        a.inset_0,
        a.border_l,
        a.border_r,
        t.atoms.border_contrast_low,
        web({
          width: 602,
          left: '50%',
          transform: [
            {translateX: '-50%'},
            {translateX: centerColumnOffset ? CENTER_COLUMN_OFFSET : 0},
            ...a.scrollbar_offset.transform,
          ],
        }),
      ]}
    />
  ) : null
}

export function RoomsScreen() {
  const pal = usePalette('default')
  const t = useTheme()
  const {_} = useLingui()
  const navigation = useNavigation<any>()
  const {hasSession, currentAccount} = useSession()
  const {isPremium, isLoading: isPremiumLoading} = usePremium()
  const {rooms, isLoading, error, refreshRooms, createRoom} = useRooms()
  const createRoomDialogControl = useDialogControl()

  if (!hasSession) {
    return (
      <Layout.Screen style={[pal.view]}>
        {isWeb && <BorderLines />}
        <Layout.Center style={styles.notLoggedInContainer}>
          <VideoCamera width={48} height={48} color={pal.colors.blue} />
          <Text style={[styles.notLoggedInText, pal.text]}>
            <Trans>Sign in to create and join video call rooms</Trans>
          </Text>
        </Layout.Center>
      </Layout.Screen>
    )
  }

  if (!isPremium && !isPremiumLoading) {
    return (
      <Layout.Screen style={[pal.view]}>
        {isWeb && <BorderLines />}
        <RoomsPaywall />
      </Layout.Screen>
    )
  }

  // Temporarily restrict Rooms to @batesky.app users
  const isDev = currentAccount?.handle?.endsWith('.batesky.app') ?? false
  if (!isDev) {
    return (
      <Layout.Screen style={[pal.view]}>
        {isWeb && <BorderLines />}
        <Layout.Center style={styles.notLoggedInContainer}>
          <VideoCameraFilled width={48} height={48} color={pal.colors.blue} />
          <Text style={[styles.comingSoonTitle, pal.text]}>
            <Trans>Group Rooms</Trans>
          </Text>
          <Text style={[styles.comingSoonSubtitle, pal.text]}>
            <Trans>Coming Soon</Trans>
          </Text>
          {currentAccount?.handle && (
            <Link
              to="/1o1"
              label={_(msg`Start 1 on 1 Room`)}
              size="large"
              variant="solid"
              color="primary"
              style={[a.rounded_md, a.justify_center, {marginTop: 24}]}>
              <ButtonIcon icon={VideoCameraFilled} position="left" />
              <ButtonText>
                <Trans>Start 1 on 1 Room</Trans>
              </ButtonText>
            </Link>
          )}
        </Layout.Center>
      </Layout.Screen>
    )
  }

  return (
    <Layout.Screen style={[pal.view]}>
      {isWeb && <BorderLines />}
      <Layout.Content>
        <View style={[styles.headerContainer, t.atoms.border_contrast_low]}>
          <Text style={[styles.headerTitle, pal.text]}>
            <Trans>Rooms</Trans>
          </Text>
        </View>

        <View
          style={[
            styles.createRoomButtonContainer,
            t.atoms.border_contrast_low,
          ]}>
          <Button
            label={_(msg`Create a new room`)}
            onPress={createRoomDialogControl.open}
            size="large"
            variant="solid"
            color="primary"
            style={[a.rounded_full]}>
            <ButtonIcon icon={EditBig} position="left" />
            <ButtonText>
              <Trans>Create Room</Trans>
            </ButtonText>
          </Button>
        </View>

        <FlatList
          data={rooms}
          renderItem={({item}) => <RoomItem room={item} />}
          keyExtractor={item => item.id}
          refreshing={isLoading}
          onRefresh={refreshRooms}
          style={styles.flatList}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Text style={[styles.emptyText, pal.textLight]}>
                {error ? (
                  <Trans>Failed to load rooms. Pull down to retry.</Trans>
                ) : (
                  <Trans>No rooms available. Create one to get started!</Trans>
                )}
              </Text>
            </View>
          }
        />

        <CreateRoomDialog
          control={createRoomDialogControl}
          onCreateRoom={async (roomName, flair) => {
            // Create the room in the backend
            const newRoom = await createRoom(roomName, flair)

            // Navigate directly to the call screen
            navigation.navigate('RoomCall', {
              roomId: newRoom.id,
            })

            return newRoom
          }}
        />
      </Layout.Content>
    </Layout.Screen>
  )
}

const styles = StyleSheet.create({
  headerContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    width: '100%',
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  createRoomButtonContainer: {
    padding: 16,
    borderBottomWidth: 1,
    width: '100%',
  },
  flatList: {
    width: '100%',
  },
  emptyContainer: {
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: 16,
    textAlign: 'center',
  },
  notLoggedInContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  notLoggedInText: {
    fontSize: 16,
    textAlign: 'center',
    marginTop: 16,
  },
  comingSoonTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    textAlign: 'center',
    marginTop: 16,
  },
  comingSoonSubtitle: {
    fontSize: 18,
    textAlign: 'center',
    marginTop: 8,
  },
})
